import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const FALLBACK = [
  { text: "ORACLE ONLINE · 7 MODES ACTIVE", severity: "LOW", color: "#7AC860" },
  { text: "COMPLIANCE GUARD ACTIVE", severity: "LOW", color: "#7AC860" },
  { text: "ADMINONE COMMAND CENTER SECURED", severity: "LOW", color: "#7AC860" },
];

export default function AdminChyron({ items: legacyItems = null, speed = "normal", severity = "normal" }) {
  const [liveItems, setLiveItems] = useState(null);
  const [errCount, setErrCount] = useState(0);
  const timerRef = useRef(null);

  // Pull /chyron/live every 25s; falls back to legacyItems then FALLBACK if unauth.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await adminApi.chyronLive(16);
        if (cancelled) return;
        if (Array.isArray(data?.items) && data.items.length) {
          setLiveItems(data.items);
          setErrCount(0);
        }
      } catch (e) {
        setErrCount((n) => n + 1);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 25000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const items = useMemo(() => {
    if (liveItems && liveItems.length) return liveItems;
    if (Array.isArray(legacyItems)) {
      // Legacy support: string[] -> [{text}]
      return legacyItems.map((t) => (typeof t === "string" ? { text: t, severity: "LOW", color: "#7AC860" } : t));
    }
    return FALLBACK;
  }, [liveItems, legacyItems]);

  const doubled = useMemo(() => [...items, ...items], [items]);

  const dur = useMemo(() => {
    if (speed === "slow") return 110;
    if (speed === "fast") return 32;
    return 60;
  }, [speed]);

  const hasExtreme = items.some((i) => i.severity === "EXTREME");
  const hasHigh = items.some((i) => i.severity === "HIGH");
  const sevCls = hasExtreme ? "is-critical" : hasHigh ? "is-warning" : severity === "critical" ? "is-critical" : severity === "warning" ? "is-warning" : "is-normal";

  return (
    <div className={`admin-chyron mesh-chyron ${sevCls}`} data-testid="admin-chyron" role="marquee" aria-label="AdminOne live ticker">
      <div className="admin-chyron__dot" aria-hidden="true" />
      <div
        className="admin-chyron-track"
        style={{ animationDuration: `${dur}s` }}
        data-testid="admin-chyron-track"
      >
        {doubled.map((it, i) => {
          const text = it?.text ?? String(it ?? "");
          const color = it?.color || "#F5C842";
          const sev = it?.severity || "LOW";
          return (
            <span className="admin-chyron-item mesh-chyron-item" key={`${text}-${i}`} style={{ color }}>
              <span className="admin-chyron-sep" aria-hidden="true" style={{ color }}>◆</span>
              <span className="mesh-chyron-sev" data-sev={sev}>{sev}</span>
              <span>{text}</span>
              {typeof it?.oracle_score === "number" && (
                <span className="mesh-chyron-score">OS {it.oracle_score}</span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
